// SPDX-License-Identifier: MIT
// GeoJSON export utility for Kepler.gl editor features

import type { KeplerEditorFeature } from '../types/kepler-features';
import type { FeatureAnnotation } from '../integrations/feature-annotations/feature-annotations-slice';

/**
 * Reserved annotation field names that cannot be used as custom property keys.
 * Custom properties with these names would silently overwrite structured annotation
 * values in the exported GeoJSON, producing incorrect output.
 */
export const RESERVED_ANNOTATION_KEYS = new Set(['label', 'tags', 'comment', 'author']);

/**
 * Merge structured annotations into a feature's GeoJSON properties.
 * Produces flat GeoJSON-friendly properties: label, tags (array), comment,
 * author, plus any custom key-value pairs.
 *
 * Custom properties whose keys collide with reserved annotation field names
 * (label, tags, comment, author) are silently skipped to prevent clobbering
 * the structured annotation values.
 */
export function mergeFeaturesWithAnnotations(
  features: KeplerEditorFeature[],
  annotations: Record<string, FeatureAnnotation>
): KeplerEditorFeature[] {
  return features.map(feature => {
    const featureId = feature.id;
    if (!featureId || !annotations[featureId]) return feature;
    const ann = annotations[featureId];
    const merged: Record<string, unknown> = { ...(feature.properties || {}) };
    if (ann.label) merged.label = ann.label;
    if (ann.tags.length > 0) merged.tags = ann.tags;
    if (ann.comment) merged.comment = ann.comment;
    if (ann.author) merged.author = ann.author;
    // Custom key-value pairs — skip keys that would overwrite structured annotation fields
    for (const [k, v] of Object.entries(ann.customProperties)) {
      if (!RESERVED_ANNOTATION_KEYS.has(k)) {
        merged[k] = v;
      }
    }
    return { ...feature, properties: merged };
  });
}

/**
 * Convert Kepler.gl editor features to a valid GeoJSON FeatureCollection
 * @param features - Array of drawn features from Kepler.gl editor
 * @returns GeoJSON FeatureCollection object
 */
export function exportFeaturesAsGeoJSON(
  features: KeplerEditorFeature[]
): GeoJSON.FeatureCollection {
  if (!features || features.length === 0) {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  // Convert each feature to GeoJSON format
  const geoJsonFeatures = features
    .filter(feature => feature && feature.geometry)
    .map((feature, index) => {
      // Ensure valid GeoJSON structure
      const geoJsonFeature: GeoJSON.Feature = {
        type: 'Feature',
        geometry: feature.geometry,
        properties: {
          // Preserve original properties (includes merged annotations)
          ...(feature.properties || {}),
          // Add metadata if not already present
          id: feature.id || feature.properties?.id || `feature-${index}`,
          // Preserve any additional Kepler-specific metadata
          ...(feature.style ? { style: feature.style } : {}),
        },
      };

      // Add id at the feature level if it exists
      if (feature.id) {
        (geoJsonFeature as any).id = feature.id;
      }

      return geoJsonFeature;
    });

  return {
    type: 'FeatureCollection',
    features: geoJsonFeatures,
  };
}

/**
 * Download GeoJSON as a file
 * @param geoJson - GeoJSON FeatureCollection to download
 * @param filename - Name of the file to download (default: 'drawn-polygons.geojson')
 */
export function downloadGeoJSON(
  geoJson: GeoJSON.FeatureCollection,
  filename = 'drawn-polygons.geojson'
): void {
  const jsonString = JSON.stringify(geoJson, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Copy GeoJSON to clipboard
 * @param geoJson - GeoJSON FeatureCollection to copy
 * @returns Promise that resolves when copied successfully
 */
export async function copyGeoJSONToClipboard(geoJson: GeoJSON.FeatureCollection): Promise<void> {
  const jsonString = JSON.stringify(geoJson, null, 2);
  await navigator.clipboard.writeText(jsonString);
}

/**
 * Validate that a feature has a valid polygon geometry
 * @param feature - Feature to validate
 * @returns true if the feature has a valid polygon geometry
 */
export function isValidPolygonFeature(feature: KeplerEditorFeature | undefined): boolean {
  if (!feature || !feature.geometry) return false;

  const geomType = feature.geometry.type;
  return geomType === 'Polygon' || geomType === 'MultiPolygon';
}

/**
 * Filter only polygon features from a feature collection
 * @param features - Array of features
 * @returns Array of only polygon features
 */
export function filterPolygonFeatures(features: KeplerEditorFeature[]): KeplerEditorFeature[] {
  return features.filter(isValidPolygonFeature);
}

// ─── Import helpers ───────────────────────────────────────────────────────────

/**
 * Non-annotation property keys that should be excluded when extracting custom
 * properties during GeoJSON import (id and style are Kepler metadata, not user data).
 */
const IMPORT_EXCLUDED_KEYS = new Set([...RESERVED_ANNOTATION_KEYS, 'id', 'style']);

export interface ParsedAnnotationsResult {
  /** Annotation map keyed by feature ID */
  annotations: Record<string, FeatureAnnotation>;
  /** Flattened tag pool from all features */
  availableTags: string[];
  /** Raw GeoJSON features (with properties intact for re-loading into Kepler editor) */
  features: KeplerEditorFeature[];
}

/**
 * Parse an annotated GeoJSON FeatureCollection (previously exported by Atlas Viewer)
 * and extract structured annotations ready for Redux state restoration.
 *
 * Only features with a stable string `id` are annotated; features without an id
 * are still returned in `features` so they can be re-loaded into the Kepler editor.
 */
export function parseAnnotationsFromGeoJSON(raw: unknown): ParsedAnnotationsResult {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid GeoJSON: expected an object');
  }
  const obj = raw as Record<string, unknown>;
  if (obj['type'] !== 'FeatureCollection' || !Array.isArray(obj['features'])) {
    throw new Error('Invalid GeoJSON: expected a FeatureCollection');
  }

  const annotations: Record<string, FeatureAnnotation> = {};
  const tagSet = new Set<string>();
  const features: KeplerEditorFeature[] = [];

  for (const rawFeature of obj['features'] as unknown[]) {
    if (!rawFeature || typeof rawFeature !== 'object') continue;
    const f = rawFeature as Record<string, unknown>;

    // Resolve feature id (prefer top-level id, fall back to properties.id)
    const props = (f['properties'] as Record<string, unknown> | null) || {};
    const rawId = f['id'] ?? props['id'];
    const featureId = rawId != null ? String(rawId) : null;
    const geometry = f['geometry'];
    const isValidGeometry =
      geometry &&
      typeof geometry === 'object' &&
      typeof (geometry as Record<string, unknown>)['type'] === 'string';

    // Skip malformed features that cannot be safely re-loaded into Kepler editor
    if (!isValidGeometry) continue;

    // Build a KeplerEditorFeature-shaped object for re-loading into the editor
    const feature: KeplerEditorFeature = {
      type: 'Feature',
      geometry: geometry as KeplerEditorFeature['geometry'],
      properties: { ...(props as Record<string, unknown>) },
      ...(featureId != null ? { id: featureId } : {}),
      ...(f['style'] ? { style: f['style'] } : {}),
    } as unknown as KeplerEditorFeature;
    features.push(feature);

    if (!featureId) continue;

    // Extract structured annotation fields
    const label = typeof props['label'] === 'string' ? props['label'] : '';
    const rawTags = props['tags'];
    const tags: string[] = Array.isArray(rawTags) ? rawTags.map(String) : [];
    const comment = typeof props['comment'] === 'string' ? props['comment'] : '';
    const author = typeof props['author'] === 'string' ? props['author'] : '';

    // Remaining keys → custom properties
    const customProperties: Record<string, string> = {};
    for (const [k, v] of Object.entries(props)) {
      if (!IMPORT_EXCLUDED_KEYS.has(k)) {
        customProperties[k] = String(v ?? '');
      }
    }

    // Only record annotation if there's at least something to restore
    if (label || tags.length || comment || author || Object.keys(customProperties).length) {
      annotations[featureId] = { label, tags, comment, author, customProperties };
      tags.forEach(t => tagSet.add(t));
    }
  }

  return { annotations, availableTags: [...tagSet], features };
}
